import { useEffect, useMemo, useState } from 'react';
import api, {
  FederationStatus,
  MarketplaceFleetApp,
  MarketplaceFleetResponse,
  extractApiError,
} from '../services/api';
// Welle 4.6 QoL-6: shared German status labels.
import { labelForFederationStatus } from '../services/status-labels';

/**
 * Marketplace fleet view (Welle 4.6 NS-3 MVP).
 *
 * Cross-tenant matrix of which marketplace apps are installed on
 * which Enterprise tenants. Rows are apps, columns are tenants,
 * cells render install state per the rules in api.ts.
 *
 * MVP scope:
 *   - Read-only view. Auto-install / auto-uninstall is a Welle
 *     4.7+ workstream (would require AWX templates) and is
 *     deferred. Per-app version diffs are deferred (needs Helm
 *     release tracking). Cross-tenant config diff is deferred.
 *   - Header stats card (total apps, total tenants, fully covered,
 *     partial coverage).
 *   - Category + coverage filters.
 *   - Click a row → modal listing tenants missing the app with
 *     their last-reachable status (so operators don't pick an
 *     offline tenant for the install they'd then do manually).
 */

type Coverage = 'full' | 'partial' | 'none';
type CoverageFilter = 'all' | Coverage;

const STATUS_COLORS: Record<FederationStatus, string> = {
  reachable: 'bg-green-100 text-green-800',
  unreachable: 'bg-amber-100 text-amber-800',
  auth_failed: 'bg-red-100 text-red-700',
  timeout: 'bg-amber-100 text-amber-800',
  disabled: 'bg-slate-200 text-slate-600',
  unknown: 'bg-slate-100 text-slate-500',
  // Local managed tenant (no federation) — authoritative selected-apps data.
  local: 'bg-sky-100 text-sky-700',
};

/**
 * Classify an app's coverage against the REACHABLE tenants only.
 * The SPA pulls the reachable list from the federation map so a
 * future backend addition of a new status string (e.g. "degraded")
 * doesn't require a SPA change to keep the filter math correct.
 */
function classifyCoverage(app: MarketplaceFleetApp, reachableCount: number): Coverage {
  if (reachableCount === 0) return 'none';
  if (app.installedOn.length === 0) return 'none';
  if (app.installedOn.length >= reachableCount) return 'full';
  return 'partial';
}

interface CellState {
  kind: 'installed' | 'missing' | 'unknown';
  status: FederationStatus;
}

function cellState(
  app: MarketplaceFleetApp,
  tenantSlug: string,
  federation: MarketplaceFleetResponse['federation'],
): CellState {
  const status = federation[tenantSlug]?.status ?? 'unknown';
  if (app.installedOn.includes(tenantSlug)) return { kind: 'installed', status };
  if (app.notInstalledOn.includes(tenantSlug)) return { kind: 'missing', status };
  // Tenant exists in `tenants[]` but is in neither installedOn
  // nor notInstalledOn — the scoped instance was unreachable.
  return { kind: 'unknown', status };
}

export function MarketplaceFleetPage() {
  const [data, setData] = useState<MarketplaceFleetResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>('all');
  const [drillApp, setDrillApp] = useState<MarketplaceFleetApp | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<MarketplaceFleetResponse>('/marketplace/fleet')
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(extractApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // "Known" tenants form the coverage denominator: a reachable federated
  // target OR a local managed tenant (authoritative selectedServices). Only
  // unreachable / auth_failed federated targets are excluded — their per-app
  // state is genuinely unknown. (Name kept for churn; semantics = "known".)
  const reachableTenants = useMemo<string[]>(() => {
    if (!data) return [];
    return data.tenants.filter((t) => {
      const s = data.federation[t]?.status;
      return s === 'reachable' || s === 'local';
    });
  }, [data]);

  const categories = useMemo<string[]>(() => {
    if (!data) return [];
    const seen = new Set<string>();
    for (const app of data.apps) seen.add(app.category);
    return Array.from(seen).sort();
  }, [data]);

  const filteredApps = useMemo<MarketplaceFleetApp[]>(() => {
    if (!data) return [];
    return data.apps.filter((app) => {
      if (categoryFilter !== 'all' && app.category !== categoryFilter) return false;
      if (coverageFilter !== 'all') {
        const c = classifyCoverage(app, reachableTenants.length);
        if (c !== coverageFilter) return false;
      }
      return true;
    });
  }, [data, categoryFilter, coverageFilter, reachableTenants.length]);

  const stats = useMemo(() => {
    if (!data) return { totalApps: 0, totalTenants: 0, fullCoverage: 0, partialCoverage: 0 };
    let full = 0;
    let partial = 0;
    for (const app of data.apps) {
      const c = classifyCoverage(app, reachableTenants.length);
      if (c === 'full') full += 1;
      else if (c === 'partial') partial += 1;
    }
    return {
      totalApps: data.apps.length,
      totalTenants: data.tenants.length,
      fullCoverage: full,
      partialCoverage: partial,
    };
  }, [data, reachableTenants.length]);

  if (loading) {
    return <div className="text-slate-500">Lade Fleet-Ansicht…</div>;
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
        Fehler beim Laden: {error}
      </div>
    );
  }
  if (!data) {
    return <div className="text-slate-500">Keine Daten verfügbar.</div>;
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Marketplace Fleet</h2>
          <p className="text-sm text-slate-500">
            Welche Marketplace-Apps sind pro Tenant ausgewählt? Enterprise-Tenants mit
            aktiver Föderation zeigen zusätzlich den real installierten Status.
          </p>
        </div>
      </header>

      <StatsCard stats={stats} reachableCount={reachableTenants.length} />

      <div className="flex flex-wrap gap-4 items-end">
        <label className="text-sm">
          <span className="block text-slate-700 mb-1">Kategorie</span>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-2 py-1 border rounded bg-white"
          >
            <option value="all">Alle</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-slate-700 mb-1">Coverage</span>
          <select
            value={coverageFilter}
            onChange={(e) => setCoverageFilter(e.target.value as CoverageFilter)}
            className="px-2 py-1 border rounded bg-white"
          >
            <option value="all">Alle</option>
            <option value="full">Vollständig (überall installiert)</option>
            <option value="partial">Teilweise (nur auf einigen)</option>
            <option value="none">Keine (nirgends installiert)</option>
          </select>
        </label>
        <div className="text-xs text-slate-500 pb-1">
          {filteredApps.length} von {data.apps.length} Apps angezeigt.
        </div>
      </div>

      {data.tenants.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 text-slate-600 px-4 py-3 rounded text-sm">
          Noch keine Tenants angelegt. Die Fleet-Ansicht zeigt die ausgewählten Apps pro
          Tenant, sobald mindestens ein Tenant existiert.
        </div>
      ) : (
        <FleetTable
          apps={filteredApps}
          tenants={data.tenants}
          federation={data.federation}
          onRowClick={setDrillApp}
        />
      )}

      {drillApp ? (
        <DrillDownModal
          app={drillApp}
          federation={data.federation}
          tenants={data.tenants}
          onClose={() => setDrillApp(null)}
        />
      ) : null}
    </div>
  );
}

interface StatsCardProps {
  stats: { totalApps: number; totalTenants: number; fullCoverage: number; partialCoverage: number };
  reachableCount: number;
}

function StatsCard({ stats, reachableCount }: StatsCardProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatTile label="Apps gesamt" value={stats.totalApps} />
      <StatTile
        label="Tenants"
        value={stats.totalTenants}
        sub={`${reachableCount} mit Daten`}
      />
      <StatTile label="Voll abgedeckt" value={stats.fullCoverage} tone="success" />
      <StatTile label="Teilweise abgedeckt" value={stats.partialCoverage} tone="warning" />
    </div>
  );
}

function StatTile({
  label, value, sub, tone,
}: { label: string; value: number; sub?: string; tone?: 'success' | 'warning' }) {
  const toneClass =
    tone === 'success' ? 'text-green-700' :
    tone === 'warning' ? 'text-amber-700' :
    'text-slate-800';
  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold ${toneClass}`}>{value}</div>
      {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
    </div>
  );
}

interface FleetTableProps {
  apps: MarketplaceFleetApp[];
  tenants: string[];
  federation: MarketplaceFleetResponse['federation'];
  onRowClick: (app: MarketplaceFleetApp) => void;
}

function FleetTable({ apps, tenants, federation, onRowClick }: FleetTableProps) {
  return (
    <div className="overflow-x-auto bg-white rounded shadow-sm border border-slate-200">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-3 py-2">App</th>
            <th className="text-left px-3 py-2">Kategorie</th>
            {tenants.map((t) => (
              <th key={t} className="text-center px-2 py-2" title={federation[t]?.status ?? 'unknown'}>
                <div>{t}</div>
                <TenantStatusBadge status={(federation[t]?.status ?? 'unknown') as FederationStatus} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {apps.length === 0 ? (
            <tr>
              <td colSpan={2 + tenants.length} className="text-center text-slate-500 py-6">
                Keine Apps passen zum Filter.
              </td>
            </tr>
          ) : apps.map((app) => (
            <tr
              key={app.slug}
              onClick={() => onRowClick(app)}
              className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
            >
              <td className="px-3 py-2">
                <div className="font-medium">{app.name}</div>
                <div className="text-xs text-slate-500 font-mono">{app.slug}</div>
              </td>
              <td className="px-3 py-2 text-slate-600">{app.category}</td>
              {tenants.map((t) => {
                const cell = cellState(app, t, federation);
                return (
                  <td key={t} className="px-2 py-2 text-center">
                    <CellGlyph kind={cell.kind} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CellGlyph({ kind }: { kind: 'installed' | 'missing' | 'unknown' }) {
  // Tailwind-only icons — no extra icon dep. Aria labels carry
  // the semantic meaning for screen readers; the glyph is the
  // visual shorthand operators learn at first glance.
  if (kind === 'installed') {
    return <span aria-label="installiert" className="text-green-700 font-semibold">✓</span>;
  }
  if (kind === 'missing') {
    return <span aria-label="nicht installiert" className="text-amber-700 font-semibold">✗</span>;
  }
  return <span aria-label="unbekannt (Tenant nicht erreichbar)" className="text-slate-400 font-semibold">?</span>;
}

function TenantStatusBadge({ status }: { status: FederationStatus }) {
  // The column-header badge tells the operator at-a-glance which
  // tenant's data they should trust. An "?" cell with an
  // "unreachable" header is just a stale read, not an actual
  // missing install — they should pull the federation page first
  // before manually installing.
  return (
    <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] ${STATUS_COLORS[status]}`}>
      {labelForFederationStatus(status)}
    </span>
  );
}

interface DrillDownModalProps {
  app: MarketplaceFleetApp;
  federation: MarketplaceFleetResponse['federation'];
  tenants: string[];
  onClose: () => void;
}

function DrillDownModal({ app, federation, tenants, onClose }: DrillDownModalProps) {
  // The drill-down's job is to answer "which tenants would I have
  // to install this on" — operators read this BEFORE manually
  // kicking the install on each one. Tenants whose scoped
  // instance is unreachable are explicitly called out so the
  // operator doesn't waste a click on an offline pod.
  const installedTenants = app.installedOn;
  const missingTenants = app.notInstalledOn;
  const unknownTenants = tenants.filter(
    (t) => !installedTenants.includes(t) && !missingTenants.includes(t),
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Detail-Ansicht für ${app.name}`}
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded shadow-xl w-full max-w-xl p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold">{app.name}</h3>
            <div className="text-xs text-slate-500 font-mono">{app.slug} · {app.category}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Schliessen">
            ✕
          </button>
        </div>

        <DrillSection
          title="Installiert"
          tone="success"
          slugs={installedTenants}
          federation={federation}
          emptyHint="Auf keinem erreichbaren Tenant installiert."
        />
        <DrillSection
          title="Fehlt"
          tone="warning"
          slugs={missingTenants}
          federation={federation}
          emptyHint="Keine fehlenden Installations-Targets."
        />
        {unknownTenants.length > 0 ? (
          <DrillSection
            title="Unbekannt (Tenant nicht erreichbar)"
            tone="muted"
            slugs={unknownTenants}
            federation={federation}
            emptyHint=""
          />
        ) : null}

        <div className="text-xs text-slate-500 mt-4 border-t border-slate-100 pt-2">
          Hinweis: MVP-Ansicht. Auto-Install pro Tenant kommt in Welle 4.7+ (NS-3 deferred scope).
        </div>
      </div>
    </div>
  );
}

interface DrillSectionProps {
  title: string;
  tone: 'success' | 'warning' | 'muted';
  slugs: string[];
  federation: MarketplaceFleetResponse['federation'];
  emptyHint: string;
}

function DrillSection({ title, tone, slugs, federation, emptyHint }: DrillSectionProps) {
  const headingTone =
    tone === 'success' ? 'text-green-700' :
    tone === 'warning' ? 'text-amber-700' :
    'text-slate-500';
  return (
    <section className="mb-4">
      <h4 className={`text-sm font-semibold ${headingTone} mb-1`}>
        {title} ({slugs.length})
      </h4>
      {slugs.length === 0 ? (
        <div className="text-xs text-slate-400">{emptyHint}</div>
      ) : (
        <ul className="space-y-1 text-sm">
          {slugs.map((s) => {
            const status = (federation[s]?.status ?? 'unknown') as FederationStatus;
            return (
              <li key={s} className="flex items-center justify-between bg-slate-50 px-2 py-1 rounded">
                <span className="font-mono text-slate-700">{s}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${STATUS_COLORS[status]}`}>
                  {status}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ===================================================================
// Nav-bar badge (Welle 4.6 NS-3).
//
// Mirrors PlanChangeRequestsNavBadge: polls the fleet endpoint
// every 30s and surfaces the partial-coverage app count as a
// numeric badge. The choice of "partial" (not "none") is
// deliberate — apps with zero coverage are usually deliberate
// (deprecated or opt-in), while apps with PARTIAL coverage are
// the actionable signal: an operator rolled out an app to some
// tenants but missed others.
// ===================================================================

const MARKETPLACE_BADGE_POLL_INTERVAL_MS = 30_000;

export function MarketplaceFleetNavBadge() {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    function load(): void {
      api
        .get<MarketplaceFleetResponse>('/marketplace/fleet')
        .then((r) => {
          if (cancelled) return;
          const reachableCount = r.data.tenants.filter((t) => {
            const s = r.data.federation[t]?.status;
            return s === 'reachable' || s === 'local';
          }).length;
          let partial = 0;
          for (const app of r.data.apps) {
            if (classifyCoverage(app, reachableCount) === 'partial') partial += 1;
          }
          setCount(partial);
        })
        .catch(() => {
          // Silent on poll failures — the main page surfaces the
          // banner-level error. A failed badge poll just hides
          // the badge.
          if (!cancelled) setCount(null);
        });
    }
    load();
    const id = window.setInterval(load, MARKETPLACE_BADGE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (count === null || count === 0) return null;
  return (
    <span
      className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold"
      aria-label={`${count} Apps mit teilweiser Coverage`}
      title={`${count} Apps mit teilweiser Coverage`}
    >
      {count}
    </span>
  );
}
